import { useCallback, useState } from "react";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";
import ModalVisorSol from "./ModalVisorSol";
import ModalVisorCostas from "./ModalVisorCostas";
import usePermiso from "../../hooks/usePermiso";

import { buildDireccionPropiedad } from "../../utils/buildDireccionPropiedad";
import { nombrePoblacionDisplay, cpPorPoblacionId } from "../../utils/poblacionesFavoritas";

/** Dirección legible; prioriza población del catálogo (poblacion_id), no texto legacy. */
export function buildPropiedadAddress(prop = {}, ctx = {}) {
  const poblacionNombre =
    ctx.poblacionNombre ||
    nombrePoblacionDisplay(prop) ||
    "";
  const provinciaNombre =
    ctx.provinciaNombre || prop.provincia_nombre || prop.provincia || "";
  const tipoViaNombre =
    ctx.tipoViaNombre || prop.tipo_via_nombre || prop.tipo_via || "";
  const codigoPostal =
    prop.codigo_postal || (prop.poblacion_id ? cpPorPoblacionId(prop.poblacion_id) : "") || "";

  const line = buildDireccionPropiedad(
    { ...prop, codigo_postal: codigoPostal },
    {
      tipoViaNombre,
      poblacionNombre,
      provinciaNombre,
    },
  );
  const sinPais = line.replace(/,?\s*España\s*$/i, "").trim();
  if (sinPais.length >= 8) return sinPais;

  const parts = [prop.nombre_via, prop.numero, poblacionNombre, codigoPostal].filter(Boolean);
  if (parts.length >= 2) return parts.join(", ");
  const dir = prop.direccion_completa || prop.direccion;
  if (dir) return String(dir).trim();
  return parts.join(", ");
}

export function propiedadCoords(prop = {}) {
  const lat = prop.latitud != null && prop.latitud !== "" ? Number(prop.latitud) : null;
  const lng = prop.longitud != null && prop.longitud !== "" ? Number(prop.longitud) : null;
  const ok =
    lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng);
  return { lat, lng, ok };
}

export function propiedadMetaFrom(prop = {}) {
  return {
    plantas_edificio: prop.plantas_edificio,
    plantas_inmueble: prop.plantas_inmueble,
    altura_planta: prop.altura_planta,
    m2_parcela: prop.m2_parcela,
    m2_construidos: prop.m2_construidos,
  };
}

/**
 * Botones Visor Costas + Visor Sol reutilizables (ficha, vista previa, tabla, ficha cliente).
 */
export default function BotonesVisorUbicacion({
  propiedad = {},
  addressContext = {},
  compact = false,
  fichaResumen = false,
  onClickStop,
}) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const { puede } = usePermiso();
  const puedeVerVisorCostas = puede("ubi_visor_costas", "ver");
  const puedeVerVisorSol = puede("ubi_visor_sol", "ver");
  const [visorSolAbierto, setVisorSolAbierto] = useState(false);
  const [visorCostasAbierto, setVisorCostasAbierto] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [geoError, setGeoError] = useState("");
  const [resolvedCoords, setResolvedCoords] = useState({ lat: null, lng: null });

  const { lat, lng, ok: coordsOk } = propiedadCoords(propiedad);
  const address = buildPropiedadAddress(propiedad, addressContext);
  const canGeocode = !coordsOk && address.trim().length >= 8;

  const wrapClick = (e) => {
    e.stopPropagation();
    onClickStop?.(e);
  };

  const resolveCoordsThen = useCallback(
    async (openFn) => {
      setGeoError("");
      if (coordsOk) {
        setResolvedCoords({ lat, lng });
        openFn();
        return;
      }
      if (!canGeocode) {
        setGeoError("Indica población y dirección, o coordenadas en Localización");
        return;
      }
      setGeocoding(true);
      try {
        const res = await api.post("/geo/geocode-direccion", { address });
        if (!res.data?.ok) {
          setGeoError("No se pudo ubicar la dirección");
          return;
        }
        setResolvedCoords({
          lat: Number(res.data.latitud),
          lng: Number(res.data.longitud),
        });
        openFn();
      } catch {
        setGeoError("Error al geocodificar");
      } finally {
        setGeocoding(false);
      }
    },
    [address, canGeocode, coordsOk, lat, lng],
  );

  const openVisorSol = useCallback(() => {
    void resolveCoordsThen(() => setVisorSolAbierto(true));
  }, [resolveCoordsThen]);

  const openVisorCostas = useCallback(() => {
    void resolveCoordsThen(() => setVisorCostasAbierto(true));
  }, [resolveCoordsThen]);

  const pad = compact ? "4px 8px" : "6px 14px";
  const baseFont = compact ? 10 : 12;
  const fontSize = fichaResumen ? baseFont + 1 : baseFont;

  const baseBtn = {
    display: "inline-flex",
    alignItems: "center",
    gap: compact ? 4 : 8,
    padding: pad,
    background: isDark ? "#1a2030" : "#f1f5f9",
    border: `1px solid ${c.border}`,
    color: c.textSecondary,
    borderRadius: 6,
    fontSize,
    fontWeight: 600,
    textDecoration: "none",
    cursor: "pointer",
    fontFamily: "inherit",
  };

  const locEnabled = coordsOk || canGeocode;
  const costasSt = {
    ...baseBtn,
    border: `1px solid ${locEnabled ? "#0ea5e966" : c.border}`,
    background: locEnabled
      ? isDark
        ? "linear-gradient(135deg, #0c4a6e 0%, #075985 100%)"
        : "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)"
      : isDark
        ? "#1a2030"
        : "#f1f5f9",
    color: locEnabled ? (isDark ? "#e0f2fe" : "#075985") : c.textMuted,
    opacity: locEnabled ? 1 : 0.65,
    cursor: locEnabled && !geocoding ? "pointer" : "not-allowed",
  };

  const solSt = {
    ...baseBtn,
    border: `1px solid ${locEnabled ? "#f59e0b66" : c.border}`,
    background: locEnabled
      ? isDark
        ? "linear-gradient(135deg, #422006 0%, #78350f 100%)"
        : "linear-gradient(135deg, #fffbeb 0%, #fde68a 100%)"
      : isDark
        ? "#1a2030"
        : "#f1f5f9",
    color: locEnabled ? (isDark ? "#fde68a" : "#92400e") : c.textMuted,
    opacity: locEnabled ? 1 : 0.65,
    cursor: locEnabled && !geocoding ? "pointer" : "not-allowed",
  };

  const activeLat = resolvedCoords.lat ?? lat;
  const activeLng = resolvedCoords.lng ?? lng;

  return (
    <>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: compact ? 6 : 10, alignItems: "center" }}
        onClick={wrapClick}
      >
        {puedeVerVisorCostas && (
          <button
            type="button"
            disabled={!locEnabled || geocoding}
            title={
              coordsOk
                ? "Mapa interactivo DPMT / servidumbres con la propiedad marcada"
                : canGeocode
                  ? "Geocodificar dirección y abrir visor costas"
                  : "Indica coordenadas o dirección completa con población"
            }
            onClick={(e) => {
              wrapClick(e);
              openVisorCostas();
            }}
            style={costasSt}
          >
            {geocoding ? "…" : compact ? "🌊 Costas" : "🔍 Abrir Visor Costas"}
          </button>
        )}
        {puedeVerVisorSol && (
          <button
            type="button"
            disabled={!locEnabled || geocoding}
            title={
              coordsOk
                ? "Simular sol y sombras en 3D"
                : canGeocode
                  ? "Geocodificar dirección y abrir visor solar"
                  : "Indica coordenadas o dirección completa con población"
            }
            onClick={(e) => {
              wrapClick(e);
              openVisorSol();
            }}
            style={solSt}
          >
            {geocoding ? "…" : compact ? "☀️ Sol" : "☀️ Abrir Visor Sol"}
          </button>
        )}
        {geoError && (
          <span style={{ fontSize: fichaResumen ? 11 : 10, color: "#ef4444", width: "100%" }}>{geoError}</span>
        )}
      </div>

      {visorCostasAbierto && (
        <ModalVisorCostas
          key={`costas-${activeLat}-${activeLng}`}
          open={visorCostasAbierto}
          onClose={() => setVisorCostasAbierto(false)}
          lat={activeLat}
          lng={activeLng}
          referencia={propiedad.referencia || ""}
          label={address}
        />
      )}

      {visorSolAbierto && (
        <ModalVisorSol
          key={`sol-${activeLat}-${activeLng}-${address}`}
          open={visorSolAbierto}
          onClose={() => setVisorSolAbierto(false)}
          lat={activeLat}
          lng={activeLng}
          referencia={propiedad.referencia || ""}
          initialAddress={address}
          propiedadMeta={propiedadMetaFrom(propiedad)}
        />
      )}
    </>
  );
}
